import { toast, types, customerPortal } from "@vilocnv/allsetra-core";
import {
  selectDrawerSelectedAccountId,
  selectReportsState
} from "app/data/selectors";
import { getHydratedMapPreferences } from "app/data/helpers/mapPreferences.helpers";
import { setFilters } from "app/features";
import { ObjectsLocationsRequestDto } from "domain/objects/entities/dtos/objects-locations-request.dto";
import getObjectDataForMapAction from "domain/objects/store/actions/get-object-data-for-map.action";
import getObjectsLocationsAction from "domain/objects/store/actions/get-objects-locations.action";
import {
  clearSelectedObject,
  setSelectedObject,
  setObjectFilters
} from "domain/objects/store/objects.slice";
import { selectObjectsState } from "domain/objects/store/selectors/objects.selector";
import { selectGeozonesState } from "domain/geozones/store/selectors/geozones.selector";
import { useAppDispatch, useAppSelector } from "hooks";
import useMapPreferencesPersistence from "hooks/useMapPreferencesPersistence";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { getGeozonesAction } from "domain/geozones/store/actions/get-geozones.action";
import { SignalRService } from "app/data/services";
import onObjectLocationReportedEventAction from "domain/objects/store/actions/on-object-location-reported-event.action";
import onObjectIconUpdatedEventAction from "domain/objects/store/actions/on-object-icon-updated-event.action";
import { selectGroupsState } from "domain/groups/store/selectors/groups.selector";
import { selectObjectTypesState } from "domain/object-types/store/selectors/object-types.selector";
import { selectKeysState } from "domain/keys/store/selectors/keys.selector";
import { getKeysAction } from "domain/keys/store/actions/get-keys.action";
import { getGroupsAction } from "domain/groups/store/actions/get-groups.action";
import { getObjectTypesAction } from "domain/object-types/store/actions/get-object-types.action";
import { FormikHelpers } from "formik";
import {
  getAddressDisplayInfo,
  getResolvedAddressFromStore
} from "app/data/helpers";
import { MapViewPreferences } from "app/data/types/mapPreferences.types";

type ObjectForMapRequestDto = customerPortal.objects.ObjectForMapRequestDto;
type GetGeozonesRequestDto = customerPortal.geozones.GetGeozonesRequestDto;
type MapToggles = Pick<
  MapViewPreferences,
  "showSatelliteMode" | "showClusters" | "showTraffic" | "showGeozones"
>;

const useDashboardMapPage = () => {
  const dispatch = useAppDispatch();
  const accountId = useAppSelector(selectDrawerSelectedAccountId);
  const [mapRef, setMapRef] = useState<google.maps.Map>();
  const [showClusters, setShowClusters] = useState(true);
  const [showSatelliteMode, setShowSatelliteMode] = useState(false);
  const [showTraffic, setShowTraffic] = useState(false);
  const [showGeozones, setShowGeozones] = useState(true);
  const [selectedTooltipMarker, setSelectedTooltipMarker] =
    useState<types.MarkerType>();
  const [selectedCluster, setSelectedCluster] = useState<any>();
  const [mounted, setMounted] = useState(false);
  const [modalFilterOpen, setModalFilterOpen] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const { geozones } = useAppSelector(selectGeozonesState);
  const { groups, isLoadingGroups } = useAppSelector(selectGroupsState);
  const { objectTypes, isLoadingObjectTypes } = useAppSelector(
    selectObjectTypesState
  );
  const { keys, isLoadingKeys } = useAppSelector(selectKeysState);
  const {
    objects,
    markers,
    isLoadingObjects,
    isLoadingObjectDetail,
    objectFilters,
    selectedObject
  } = useAppSelector(selectObjectsState);
  const { resolvedAddresses } = useAppSelector(selectReportsState);

  const { mergeTogglesSync, mergeFiltersSync, getCachedSync, canPersist } =
    useMapPreferencesPersistence();

  const togglesRef = useRef<MapToggles>({
    showSatelliteMode: false,
    showClusters: true,
    showTraffic: false,
    showGeozones: true
  });
  const preferencesHydratedForAccountRef = useRef<string | null>(null);
  const pendingFiltersRef = useRef<types.IMapFilter | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useLayoutEffect(() => {
    if (!accountId) {
      preferencesHydratedForAccountRef.current = null;
      pendingFiltersRef.current = null;
      return;
    }

    if (!canPersist) return;

    const { toggles, filters } = getHydratedMapPreferences(getCachedSync());

    setShowSatelliteMode(toggles.showSatelliteMode);
    setShowClusters(toggles.showClusters);
    setShowTraffic(toggles.showTraffic);
    setShowGeozones(toggles.showGeozones);
    togglesRef.current = toggles;

    dispatch(setObjectFilters(filters));
    dispatch(setFilters(filters));
    pendingFiltersRef.current = filters;
    preferencesHydratedForAccountRef.current = accountId;
  }, [accountId, canPersist, getCachedSync, dispatch]);

  togglesRef.current = {
    showSatelliteMode,
    showClusters,
    showTraffic,
    showGeozones
  };

  const persistToggles = useCallback(
    (toggles: MapToggles) => {
      if (!canPersist) return;
      togglesRef.current = toggles;
      mergeTogglesSync(toggles);
    },
    [canPersist, mergeTogglesSync]
  );

  useEffect(() => {
    if (!mounted) return;

    if (!accountId) {
      toast.info("Kindly select an account from the navigation drawer.");
      return;
    }

    if (canPersist && preferencesHydratedForAccountRef.current !== accountId) {
      return;
    }

    SignalRService.hubConnection?.on("EventRaised", liveUpdateObject);

    const filters = pendingFiltersRef.current ?? objectFilters;
    pendingFiltersRef.current = null;

    dispatchObjects(filters);
    dispatchGeozones();
    dispatchGroups();
    dispatchKeys();
    dispatchObjectTypes();

    return () => {
      SignalRService.hubConnection?.off("EventRaised", liveUpdateObject);
    };
  }, [mounted, accountId, objectFilters, canPersist]);

  const liveUpdateObject = useCallback(
    (event: any) => {
      if (modalFilterOpen || isSelectOpen) return;

      if (selectedObject) {
        const latitude = selectedObject?.location?.latitude;
        const longitude = selectedObject?.location?.longitude;
        const objectResolvedAddress = selectedObject?.location?.resolvedAddress;

        const finalResolvedAddress = getResolvedAddressFromStore(
          objectResolvedAddress,
          resolvedAddresses || [],
          latitude,
          longitude
        );

        const addressInfo = getAddressDisplayInfo(
          latitude,
          longitude,
          finalResolvedAddress
        );

        if (addressInfo.isHidden) return;
      }

      switch (event?.eventName) {
        case types.BackendEventsEnum.ObjectLocationReportedEvent:
          dispatch(
            onObjectLocationReportedEventAction({
              objectLocationEventData: event,
              accountId
            })
          );
          break;
        case types.BackendEventsEnum.ObjectIconUpdatedEvent:
          dispatch(
            onObjectIconUpdatedEventAction({
              objectIconUpdatedEventData: event
            })
          );
          break;

        default:
          break;
      }
    },
    [
      dispatch,
      accountId,
      modalFilterOpen,
      isSelectOpen,
      selectedObject,
      resolvedAddresses
    ]
  );

  const dispatchObjects = async (filters: types.IMapFilter) => {
    const requestData: ObjectsLocationsRequestDto = {
      accountId: accountId ?? "",
      filters: {
        ...filters
      }
    };

    return dispatch(getObjectsLocationsAction(requestData));
  };

  const dispatchGeozones = () => {
    const requestData: GetGeozonesRequestDto = {
      accountId: accountId ?? ""
    };

    dispatch(getGeozonesAction(requestData));
  };

  const dispatchKeys = () => {
    const requestData: GetGeozonesRequestDto = {
      accountId: accountId ?? ""
    };

    dispatch(getKeysAction(requestData));
  };

  const dispatchGroups = () => {
    const requestData: GetGeozonesRequestDto = {
      accountId: accountId ?? ""
    };

    dispatch(getGroupsAction(requestData));
  };

  const dispatchObjectTypes = () => {
    const requestData: GetGeozonesRequestDto = {
      accountId: accountId ?? ""
    };

    dispatch(getObjectTypesAction(requestData));
  };

  const handleMapLoad = (map: google.maps.Map) => {
    if (map) {
      setMapRef(map);
    }
  };

  const handleMarkerMouseOver = (uniqueId: string) => {
    setSelectedTooltipMarker(undefined);
    const marker = markers.find((m) => m?.uniqueId === uniqueId);
    if (marker && marker.position && marker.name) {
      setSelectedTooltipMarker(marker);
    }
  };

  const handleMarkerMouseOut = () => {
    setSelectedTooltipMarker(undefined);
  };

  const handleShowInfoWindowClick = (uniqueId: string) => {
    if (!uniqueId) return;

    dispatch(setSelectedObject({ uniqueId }));

    const requestData: ObjectForMapRequestDto = {
      accountId: accountId ?? "",
      objectId: uniqueId
    };
    dispatch(getObjectDataForMapAction(requestData));
  };

  const handleCloseInfoWindowClick = () => {
    dispatch(clearSelectedObject());
  };

  const handleClusterClick = (cluster: any) => {
    setSelectedCluster(cluster);
  };

  const handleClusterCloseInfoWindowClick = () => {
    setSelectedCluster(undefined);
  };

  const handleShowSatelliteButtonClick = () => {
    const next = !showSatelliteMode;
    setShowSatelliteMode(next);
    persistToggles({
      showSatelliteMode: next,
      showClusters,
      showTraffic,
      showGeozones
    });
  };

  const handleShowClustersButtonClick = () => {
    const next = !showClusters;
    setShowClusters(next);
    persistToggles({
      showSatelliteMode,
      showClusters: next,
      showTraffic,
      showGeozones
    });
  };

  const handleShowTrafficButtonClick = () => {
    const next = !showTraffic;
    setShowTraffic(next);
    persistToggles({
      showSatelliteMode,
      showClusters,
      showTraffic: next,
      showGeozones
    });
  };

  const handleShowGeozonesButtonClick = () => {
    const next = !showGeozones;
    setShowGeozones(next);
    persistToggles({
      showSatelliteMode,
      showClusters,
      showTraffic,
      showGeozones: next
    });
  };

  const handleFilterModalCloseClick = () => {
    setModalFilterOpen(!modalFilterOpen);
  };

  const handleFilterModalOpenClick = () => {
    setModalFilterOpen(!modalFilterOpen);
  };

  const handleModalFilterFormSubmit = async (
    values: types.IMapFilter,
    formikHelpers: FormikHelpers<types.IMapFilter>
  ) => {
    formikHelpers.setSubmitting(true);

    const { type } = await dispatchObjects(values);

    if (type === "objects/v2/getObjectsLocation/fulfilled") {
      formikHelpers.setSubmitting(false);
      setModalFilterOpen(false);
      dispatch(setObjectFilters(values));

      if (canPersist) {
        mergeFiltersSync(values);
      }
    }
  };

  const handleFilterChange = (value: string) => {
    if (!value) return;

    const objectIndex = objects.findIndex((obj) => obj.uniqueId === value);
    if (objectIndex < 0) return;

    const selectedObjectLocation = objects[objectIndex].location;

    if (
      selectedObjectLocation &&
      selectedObjectLocation.latitude !== null &&
      selectedObjectLocation.longitude !== null
    ) {
      mapRef?.setZoom(14);
      mapRef?.panTo({
        lat: selectedObjectLocation.latitude,
        lng: selectedObjectLocation.longitude
      });
    }

    handleShowInfoWindowClick(value);
  };

  const addressInfo = selectedObject
    ? getAddressDisplayInfo(
        selectedObject?.location?.latitude,
        selectedObject?.location?.longitude,
        getResolvedAddressFromStore(
          selectedObject?.location?.resolvedAddress,
          resolvedAddresses || [],
          selectedObject?.location?.latitude,
          selectedObject?.location?.longitude
        )
      )
    : undefined;

  return {
    objectFilters,
    markers,
    showClusters,
    showSatelliteMode,
    showTraffic,
    geozones,
    showGeozones,
    groups,
    isLoadingGroups,
    keys,
    isLoadingKeys,
    objectTypes,
    isLoadingObjectTypes,
    isLoadingObjects,
    isLoadingObjectDetail,
    selectedTooltipMarker,
    selectedObject,
    selectedCluster,
    modalFilterOpen,
    isSelectOpen,
    addressInfo,
    setIsSelectOpen,
    handleMapLoad,
    handleMarkerMouseOver,
    handleMarkerMouseOut,
    handleShowInfoWindowClick,
    handleCloseInfoWindowClick,
    handleClusterClick,
    handleClusterCloseInfoWindowClick,
    handleShowSatelliteButtonClick,
    handleShowClustersButtonClick,
    handleShowTrafficButtonClick,
    handleShowGeozonesButtonClick,
    handleFilterModalCloseClick,
    handleFilterModalOpenClick,
    handleModalFilterFormSubmit,
    handleFilterChange
  };
};

export default useDashboardMapPage;
